import { Checkbox, FormControlLabel } from '@mui/material';
import XMultiImageUpload from "./XMultiImageUpload";
import XInput from '../../../../components/common/XInput';
import XSelect from '../../../../components/common/XSelect';
import XResizableTextarea from '../../../../components/common/XResizableTextarea';
import { handleEnterKeyPress } from "../../../../utils/handleEnterKey";
import Constants from '../../../../Constants';
import usePainAndSufferingForm from '../../../../hooks/usePainAndSufferingForm';
import { useProductContext } from '../../../../contexts/ProductContext';
import CurrencyInput from '../../../../components/common/CurrencyInput';
import XCheckbox from '../../../../components/common/XCheckbox';
import { formTitleClasses, formBackgroundClasses, formItemPdClasses, reportSectionClasses } from '../../../../components/common/formClasses';
import { TOUR_TARGETS } from '../../../../constants/tourTargets';

const wrapperClasses = 'p-0!';

const sectionTitleClasses = `${formTitleClasses} mb-6 text-2xl`;

const analysisHeadingClasses = `${formTitleClasses} mb-6 text-2xl pt-6 mt-2 border-t border-solid border-border-light`;

const painPointsContainerClasses = 'form-group mb-10!';

const painPointsRowClasses = `row ${reportSectionClasses} p-6 [row-gap:60px] m-0!`;

const painPointTitleClasses = `${formTitleClasses} mb-4`;

const descriptionGroupClasses = 'form-group mb-10!';

const photosRowClasses = 'row mb-4!';

const painPointCheckboxSx = { '& .MuiSvgIcon-root': { fontSize: 28, color: 'var(--color-primary)' } };

const PainAndSufferingAnalysisForm = () => {
    const { painPointsOverride } = useProductContext();
    const {
        selectedTiffIndex,
        painPoints,
        isLoading,
        painSufferingPaths,
        getFieldProps,
        currentPainAndSuffering,
        bodilyInjuriesImageFiles,
        handleKeyDown,
        bodilyInjuriesFilesOnChange,
        bodilyInjuriesIsSelectedChangeHandler,
        bodilyInjuriesRemoveHandler,
        handlePainPointToggle,
        handlePastTotalToggle,
        handleFutureTotalToggle,
        textareaRef,
    } = usePainAndSufferingForm();

    return (
        <div data-testid="pain-and-suffering-form" className={wrapperClasses} onKeyDown={(e) => handleEnterKeyPress(e)}>
            <div className={`${formBackgroundClasses} ${formItemPdClasses}`}>
                <h3 className={sectionTitleClasses}>Pain and Suffering Calculation</h3>

                <div className="row">
                    <div className="col-md-6">
                        <div className="form-group">
                            <XInput
                                label="Age"
                                placeholder="Write age..."
                                type="number"
                                defaultValue={0}
                                {...getFieldProps(painSufferingPaths.age)}
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group">
                            <XSelect
                                label="Gender"
                                options={Constants.Dropdowns.MrMrs}
                                {...getFieldProps(painSufferingPaths.gender, { useDebounce: false })}
                            />
                        </div>
                    </div>

                    <div className="col-md-6">
                        <div className="form-group">
                            <XCheckbox
                                name="isPastTotalAmount"
                                label={currentPainAndSuffering?.isPastTotalAmount ? "Past Total Non-Economic Amount" : "Past Monthly Non-Economic Amount"}
                                checked={currentPainAndSuffering?.isPastTotalAmount || false}
                                onChange={handlePastTotalToggle}
                                className={"mb-3"}
                            />
                            <CurrencyInput
                                placeholder="Enter amount"
                                {...getFieldProps(currentPainAndSuffering?.isPastTotalAmount
                                    ? painSufferingPaths.totalPastAmount
                                    : painSufferingPaths.monthlyAmount)}
                            />
                        </div>
                    </div>
                    <div className="col-md-6">
                        <div className="form-group">
                            <XCheckbox
                                name="isFutureTotalAmount"
                                label={currentPainAndSuffering?.isFutureTotalAmount ? "Future Total Non-Economic Amount" : "Future Annual Non-Economic Amount"}
                                checked={currentPainAndSuffering?.isFutureTotalAmount || false}
                                onChange={handleFutureTotalToggle}
                                className={"mb-3"}
                            />
                            <CurrencyInput
                                placeholder="Enter amount"
                                {...getFieldProps(currentPainAndSuffering?.isFutureTotalAmount
                                    ? painSufferingPaths.totalFutureAmount
                                    : painSufferingPaths.annualAmount)}
                            />
                        </div>
                    </div>
                </div>
                <h2 className={analysisHeadingClasses}>Pain and Suffering Analysis</h2>
                <div className={painPointsContainerClasses} data-tour={TOUR_TARGETS.PAIN_POINTS}>
                    <div className={painPointsRowClasses}>
                        {(painPointsOverride ?? painPoints).map((item, index) => (
                            <div className="col-sm-6 col-md-4" key={index}>
                                <p className={painPointTitleClasses}>{item.category}</p>
                                {item.points?.map((point, pointIndex) => (
                                    <div key={pointIndex}>
                                        <FormControlLabel
                                            label={point}
                                            control={<Checkbox
                                                sx={painPointCheckboxSx}
                                                checked={currentPainAndSuffering?.selectedPainPoints?.includes(point) || false}
                                                onChange={() => handlePainPointToggle(point)}
                                            />}
                                        />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
                <div className={descriptionGroupClasses}>
                    <XResizableTextarea
                        label="Describe any significant ways that your client's life continues to be limited or affected presently as a result of any ongoing conditions from the incident"
                        placeholder="Write here..."
                        onKeyDown={handleKeyDown}
                        textareaRef={textareaRef}
                        {...getFieldProps(painSufferingPaths.descriptionText)}
                    />
                </div>
                <div className={photosRowClasses} data-tour={TOUR_TARGETS.INJURY_PHOTOS}>
                    <div className="col-md-12">
                        <XMultiImageUpload
                            subHeading="Bodily Injury Photos"
                            label="Bodily Injury Photos"
                            name={`clients[${selectedTiffIndex}].painAndSuffering.bodilyInjuriesImageFiles`}
                            onChange={bodilyInjuriesFilesOnChange}
                            initialFiles={bodilyInjuriesImageFiles}
                            isLoading={isLoading}
                            onIsSelectedChange={bodilyInjuriesIsSelectedChangeHandler}
                            onRemove={bodilyInjuriesRemoveHandler}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PainAndSufferingAnalysisForm;
